import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useDarkMode } from '../../../../context/DarkModeContext';
import { ReviewPrepMetrics } from '../../../../hooks/useInternshipReviewPrep';
import { CheckCircleIcon, ExclamationTriangleIcon, DocumentTextIcon, SparklesIcon, LightBulbIcon, ClipboardDocumentIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useTalkingPoints, TalkingPoint, TalkingPointsResponse, GuidanceSuggestion } from '../../../../hooks/useTalkingPoints';
import { useCareerCards } from '../../../../hooks/useCareerCards';
import { useContentLinks } from '../../../../hooks/useContentLinks';
import { CareerFocusWithMetrics } from '../../../../hooks/useCareerFocus';
import { useToast } from '../../../../context/ToastContext';

interface EvidenceReviewStepProps {
  metrics: ReviewPrepMetrics;
  focus: CareerFocusWithMetrics;
  onNext: () => void;
  onAITalkingPointsGenerated?: (rubricItemId: string, response: TalkingPointsResponse) => void;
}

const EvidenceReviewStep: React.FC<EvidenceReviewStepProps> = ({ metrics, focus, onNext, onAITalkingPointsGenerated }) => {
  const { darkMode } = useDarkMode();
  const { generateTalkingPoints, isGenerating } = useTalkingPoints();
  const { cards } = useCareerCards();
  const { getFilteredLinks } = useContentLinks();
  const { showToast } = useToast();
  const [generatingForId, setGeneratingForId] = useState<string | null>(null);
  const [generatedResponses, setGeneratedResponses] = useState<Record<string, TalkingPointsResponse>>({});
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  
  const bgColor = darkMode ? 'bg-gray-800' : 'bg-gray-50';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const cardBgColor = darkMode ? 'bg-gray-900' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  
  // Group coverage by status
  const coverageByStatus = useMemo(() => {
    const grouped = {
      strong: [] as typeof metrics.rubricCoverage,
      adequate: [] as typeof metrics.rubricCoverage,
      weak: [] as typeof metrics.rubricCoverage,
      critical: [] as typeof metrics.rubricCoverage
    };
    
    metrics.rubricCoverage.forEach(item => {
      grouped[item.status].push(item);
    });
    
    return grouped;
  }, [metrics.rubricCoverage]);
  
  const statusConfig = {
    strong: {
      icon: CheckCircleIcon,
      color: 'text-green-500',
      bgColor: darkMode ? 'bg-green-900/20' : 'bg-green-50',
      borderColor: darkMode ? 'border-green-800' : 'border-green-200',
      label: 'Strong Evidence',
      description: 'Great job! These areas are well-documented.'
    },
    adequate: {
      icon: CheckCircleIcon,
      color: 'text-blue-500',
      bgColor: darkMode ? 'bg-blue-900/20' : 'bg-blue-50',
      borderColor: darkMode ? 'border-blue-800' : 'border-blue-200',
      label: 'Good Coverage',
      description: 'Solid evidence, consider adding more examples.'
    },
    weak: {
      icon: ExclamationTriangleIcon,
      color: 'text-yellow-500',
      bgColor: darkMode ? 'bg-yellow-900/20' : 'bg-yellow-50',
      borderColor: darkMode ? 'border-yellow-800' : 'border-yellow-200',
      label: 'Needs Attention',
      description: 'Add more evidence to strengthen these areas.'
    },
    critical: {
      icon: ExclamationTriangleIcon,
      color: 'text-red-500',
      bgColor: darkMode ? 'bg-red-900/20' : 'bg-red-50',
      borderColor: darkMode ? 'border-red-800' : 'border-red-200',
      label: 'Critical Gaps',
      description: 'No evidence yet - prioritize these areas.'
    }
  };
  
  // Get evidence cards for a rubric item
  const getEvidenceForRubricItem = async (rubricItemId: string) => {
    if (!cards || cards.length === 0) return [];
    
    // Get all links for this rubric item
    const links = await getFilteredLinks({
      targetType: 'rubric_criterion',
      targetId: rubricItemId
    });
    
    // Get the card IDs that are linked
    const linkedCardIds = links
      .filter(link => link.source_type === 'career_card')
      .map(link => link.source_id);
    
    // Filter cards to only those linked to this rubric item
    return cards.filter(card => linkedCardIds.includes(card.id));
  };
  
  // Handle AI talking points generation
  const handleGenerateAI = async (rubricItem: { rubricItemId: string; category: string; description: string }) => {
    setGeneratingForId(rubricItem.rubricItemId);
    
    try {
      const evidenceCards = await getEvidenceForRubricItem(rubricItem.rubricItemId);
      
      const result = await generateTalkingPoints({
        rubricCriterion: {
          id: rubricItem.rubricItemId,
          category: rubricItem.category,
          description: rubricItem.description
        },
        evidenceCards,
        userContext: {
          focusTitle: focus.title,
          companyName: focus.company_name,
          industryType: focus.industry_type || 'technology',
          currentWeek: Math.ceil((Date.now() - new Date(focus.start_date).getTime()) / (7 * 24 * 60 * 60 * 1000)),
          totalWeeks: Math.ceil((new Date(focus.end_date).getTime() - new Date(focus.start_date).getTime()) / (7 * 24 * 60 * 60 * 1000)),
          teamDescription: focus.team_description
        }
      });
      
      if (result) {
        setGeneratedResponses(prev => ({
          ...prev,
          [rubricItem.rubricItemId]: result
        }));
        
        // Auto-expand the generated content
        setExpandedItems(prev => ({
          ...prev,
          [rubricItem.rubricItemId]: true
        }));
        
        // Notify parent component if callback provided
        if (onAITalkingPointsGenerated) {
          onAITalkingPointsGenerated(rubricItem.rubricItemId, result);
        }
      }
    } finally {
      setGeneratingForId(null);
    }
  };
  
  // Helper to copy content to clipboard
  const copyToClipboard = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      showToast('Copied to clipboard!', 'success');
    } catch (err) {
      console.error('Failed to copy:', err);
      showToast('Failed to copy to clipboard', 'error');
    }
  };
  
  // Helper to get icon for guidance type
  const getGuidanceIcon = (promptType: GuidanceSuggestion['promptType']) => {
    switch (promptType) {
      case 'reflection-question':
        return '❓';
      case 'evidence-prompt':
        return '📝';
      case 'action-item':
        return '✅';
    }
  };
  
  // Helper to format STAR content for copying
  const formatSTARForCopy = (point: TalkingPoint) => {
    return `STAR Format:\n\nSituation: ${point.situation}\n\nTask: ${point.task}\n\nAction: ${point.action}\n\nResult: ${point.result}`;
  };
  
  // Helper to format guidance for copying
  const formatGuidanceForCopy = (suggestions: GuidanceSuggestion[]) => {
    return suggestions.map(s => `${getGuidanceIcon(s.promptType)} ${s.content}`).join('\n\n');
  };
  
  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="grid grid-cols-4 gap-4">
        {Object.entries(coverageByStatus).map(([status, items]) => {
          const config = statusConfig[status as keyof typeof statusConfig];
          const Icon = config.icon;
          
          return (
            <motion.div
              key={status}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`${cardBgColor} rounded-lg border ${borderColor} p-4 text-center`}
            >
              <Icon className={`w-8 h-8 mx-auto mb-2 ${config.color}`} />
              <div className={`text-2xl font-bold ${textColor}`}>{items.length}</div>
              <div className={`text-sm ${mutedTextColor}`}>{config.label}</div>
            </motion.div>
          );
        })}
      </div>
      
      {/* Detailed Review by Status */}
      <div className="space-y-6">
        {Object.entries(coverageByStatus).map(([status, items]) => {
          if (items.length === 0) return null;
          
          const config = statusConfig[status as keyof typeof statusConfig];
          
          return (
            <motion.div
              key={status}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className={`${config.bgColor} rounded-lg border ${config.borderColor} p-6`}
            >
              <div className="flex items-start gap-3 mb-4">
                <config.icon className={`w-6 h-6 ${config.color} flex-shrink-0`} />
                <div>
                  <h4 className={`font-semibold ${textColor}`}>{config.label}</h4>
                  <p className={`text-sm ${mutedTextColor}`}>{config.description}</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {items.map((item, index) => (
                  <motion.div
                    key={item.rubricItemId}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className={`${cardBgColor} rounded-md p-4 border ${borderColor}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className={`font-medium text-sm ${textColor}`}>{item.category}</p>
                        <p className={`text-sm ${mutedTextColor} mt-1`}>{item.description}</p>
                        <div className={`flex items-center justify-between mt-2`}>
                          <div className={`flex items-center gap-4 text-xs ${mutedTextColor}`}>
                            <span className="flex items-center gap-1">
                              <DocumentTextIcon className="w-3 h-3" />
                              {item.evidenceCount} {item.evidenceCount === 1 ? 'card' : 'cards'}
                            </span>
                            {item.lastEvidenceDate && (
                              <span>
                                Last updated {item.daysSinceLastEvidence} {item.daysSinceLastEvidence === 1 ? 'day' : 'days'} ago
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => handleGenerateAI(item)}
                            disabled={isGenerating && generatingForId === item.rubricItemId}
                            className={`text-xs px-3 py-1 rounded-md transition-colors flex items-center gap-1 ${
                              darkMode 
                                ? 'bg-purple-700 hover:bg-purple-600 text-purple-200 disabled:bg-gray-700 disabled:text-gray-500' 
                                : 'bg-purple-100 hover:bg-purple-200 text-purple-700 disabled:bg-gray-200 disabled:text-gray-400'
                            }`}
                          >
                            {item.evidenceCount === 0 ? (
                              <>
                                <LightBulbIcon className="w-3 h-3" />
                                {isGenerating && generatingForId === item.rubricItemId ? 'Getting Guidance...' : 'Get Guidance'}
                              </>
                            ) : (
                              <>
                                <SparklesIcon className="w-3 h-3" />
                                {isGenerating && generatingForId === item.rubricItemId ? 'Generating...' : 'Generate Talking Points'}
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                      <div className={`text-lg font-semibold ${config.color}`}>
                        {Math.round(item.coveragePercent)}%
                      </div>
                    </div>
                    
                    {/* Show generated content */}
                    {generatedResponses[item.rubricItemId] && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className={`mt-3 pt-3 border-t ${borderColor}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className={`text-xs font-medium ${mutedTextColor} flex items-center gap-1`}>
                            {generatedResponses[item.rubricItemId].type === 'guidance' ? (
                              <>
                                <LightBulbIcon className="w-3 h-3" />
                                Preparation Guidance
                              </>
                            ) : (
                              <>
                                <SparklesIcon className="w-3 h-3" />
                                AI-Generated Talking Points ({'points' in generatedResponses[item.rubricItemId] ? generatedResponses[item.rubricItemId].points.length : 0})
                              </>
                            )}
                          </p>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                const response = generatedResponses[item.rubricItemId];
                                const content = response.type === 'guidance' && 'suggestions' in response
                                  ? formatGuidanceForCopy(response.suggestions)
                                  : 'points' in response && response.points
                                  ? response.points.map(p => formatSTARForCopy(p)).join('\n\n---\n\n')
                                  : '';
                                copyToClipboard(content);
                              }}
                              className={`text-xs px-2 py-1 rounded transition-colors flex items-center gap-1 ${
                                darkMode 
                                  ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-300' 
                                  : 'hover:bg-gray-100 text-gray-600 hover:text-gray-700'
                              }`}
                            >
                              <ClipboardDocumentIcon className="w-3 h-3" />
                              Copy
                            </button>
                            <button
                              onClick={() => setExpandedItems(prev => ({
                                ...prev,
                                [item.rubricItemId]: !prev[item.rubricItemId]
                              }))}
                              className={`text-xs p-1 rounded transition-colors ${
                                darkMode 
                                  ? 'hover:bg-gray-700 text-gray-400' 
                                  : 'hover:bg-gray-100 text-gray-600'
                              }`}
                            >
                              {expandedItems[item.rubricItemId] ? (
                                <ChevronUpIcon className="w-3 h-3" />
                              ) : (
                                <ChevronDownIcon className="w-3 h-3" />
                              )}
                            </button>
                          </div>
                        </div>
                        
                        {expandedItems[item.rubricItemId] && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.1 }}
                            className={`space-y-2`}
                          >
                            {/* Disclaimer */}
                            <p className={`text-xs ${mutedTextColor} italic mb-3`}>
                              {generatedResponses[item.rubricItemId].disclaimer}
                            </p>
                            
                            {/* Content based on type */}
                            {generatedResponses[item.rubricItemId].type === 'guidance' && 'suggestions' in generatedResponses[item.rubricItemId] ? (
                              <div className="space-y-2">
                                {generatedResponses[item.rubricItemId].suggestions.map((suggestion, idx) => (
                                  <div key={idx} className={`text-xs p-2 rounded ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                                    <div className="flex items-start gap-2">
                                      <span className="text-base">{getGuidanceIcon(suggestion.promptType)}</span>
                                      <div className="flex-1">
                                        <p className={textColor}>{suggestion.content}</p>
                                        {suggestion.examples && suggestion.examples.length > 0 && (
                                          <div className={`mt-1 ml-2 text-xs ${mutedTextColor}`}>
                                            {suggestion.examples.map((ex, exIdx) => (
                                              <p key={exIdx}>• {ex}</p>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : 'points' in generatedResponses[item.rubricItemId] && generatedResponses[item.rubricItemId].points ? (
                              <div className="space-y-3">
                                {generatedResponses[item.rubricItemId].points.map((point, idx) => (
                                  <div key={idx} className={`text-xs p-3 rounded ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className={`font-medium ${textColor}`}>STAR Format</span>
                                        {point.isPrompt && (
                                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                                            darkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
                                          }`}>
                                            Suggested
                                          </span>
                                        )}
                                      </div>
                                      <p><span className={`font-medium ${mutedTextColor}`}>Situation:</span> <span className={textColor}>{point.situation}</span></p>
                                      <p><span className={`font-medium ${mutedTextColor}`}>Task:</span> <span className={textColor}>{point.task}</span></p>
                                      <p><span className={`font-medium ${mutedTextColor}`}>Action:</span> <span className={textColor}>{point.action}</span></p>
                                      <p><span className={`font-medium ${mutedTextColor}`}>Result:</span> <span className={textColor}>{point.result}</span></p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : null}
                            
                            {/* Tips */}
                            {generatedResponses[item.rubricItemId].tips.length > 0 && (
                              <div className={`mt-3 pt-2 border-t ${borderColor}`}>
                                <p className={`text-xs font-medium ${mutedTextColor} mb-1`}>💡 Tips:</p>
                                <ul className={`text-xs ${mutedTextColor} space-y-0.5`}>
                                  {generatedResponses[item.rubricItemId].tips.map((tip, idx) => (
                                    <li key={idx}>• {tip}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </motion.div>
                        )}
                      </motion.div>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          );
        })}
      </div>
      
      {/* Action Summary */}
      <div className={`${cardBgColor} rounded-lg border ${borderColor} p-6`}>
        <h4 className={`font-semibold mb-3 ${textColor}`}>Review Summary</h4>
        <div className={`space-y-2 text-sm ${mutedTextColor}`}>
          <p>• Overall coverage: <span className="font-semibold">{metrics.overallCoverageScore}%</span></p>
          <p>• Total evidence cards: <span className="font-semibold">{metrics.totalCards}</span></p>
          <p>• Average activity: <span className="font-semibold">{metrics.cardsPerWeek.toFixed(1)} cards/week</span></p>
          
          {/* Category breakdown */}
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <p className="font-medium mb-2">Coverage by Category:</p>
            {Object.entries(
              metrics.rubricCoverage.reduce((acc, item) => {
                if (!acc[item.category]) {
                  acc[item.category] = { total: 0, covered: 0 };
                }
                acc[item.category].total++;
                if (item.status !== 'critical') {
                  acc[item.category].covered++;
                }
                return acc;
              }, {} as Record<string, { total: number; covered: number }>)
            ).map(([category, stats]) => (
              <p key={category} className="ml-4">
                • {category}: <span className={stats.covered === stats.total ? 'text-green-600 dark:text-green-400' : ''}>
                  {stats.covered}/{stats.total} items covered
                </span>
              </p>
            ))}
          </div>
          
          {metrics.criticalGaps.length > 0 && (
            <p className="text-red-500 mt-3">⚠️ Critical gaps: {metrics.criticalGaps.join(', ')}</p>
          )}
        </div>
      </div>
      
      {/* Next Button */}
      <div className="flex justify-end">
        <button
          onClick={onNext}
          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          Continue to Reflection
        </button>
      </div>
    </div>
  );
};

export default EvidenceReviewStep;